import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { ChevronDownRegular } from "@fluentui/react-icons";
import { Text, Dropdown, IconButton } from "@fluentui/react";
import { BookTemplate20Filled } from "@fluentui/react-icons";
import { FormatClearOutlined } from "@mui/icons-material";
import { Button } from "@fluentui/react-components";
import { Box } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import i18next from "../../i18n/i18n";
import { appInfo, userInfo } from "../../authConfig";
import { ReasoningEffort, getReasoningEffortsForModel, isReasoningModel } from "../../api/models";
import tabstyles from "../../pages/chat/Chat.module.css";
import toolbarStyles from "./ToolBarProductSettingOptions.module.css";
import { Joyride, STATUS, Step } from "react-joyride";
import { useSetState } from "../../hooks/useSetState";
import { QuestionInputReducerAction } from "../../Store/QuestionInputReducer";
import { selectSuggestionsStage } from "../../Store/selectors";
import AIWorks_Guides from "../../assets/AIWORKS_Guidance.pdf";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../Store";
import { ProductFilterDropdown } from "../../pages/chat/components/ProductFilterDropdown";
import { SettingsButton } from "../SettingsButton";
import { resolveProductLocale } from "../../pages/chat/utils/sessionHelpers";

interface Product {
    manual_key: string;
    faq_key: string;
    text: string;
    isSelectable?: string;
    is_sub_only?: string;
    versions?: any[];
}

interface SubProduct {
    manual_sub_key: string;
    faq_sub_key: string;
    text: string;
}

interface ProductVersion {
    manual_version_key: string;
    faq_version_key: string;
    text: string;
    sub_versions?: SubProduct[];
}

interface ProductIndexes {
    manual_key: string;
    faq_key: string;
}

interface SubProductIndexes {
    manual_sub_key: string;
    faq_sub_key: string;
}

interface ProductVersionIndexes {
    manual_version_key: string;
    faq_version_key: string;
}

interface Props {
    products: any;
    productText: string;
    subProductText: string;
    productVersionText: string;
    productIndexes?: ProductIndexes;
    subProductIndexes: SubProductIndexes;
    productVersionIndexes?: ProductVersionIndexes;
    windowSize: { width: number; height: number };
    shouldDisableInputBox: () => boolean;
    onProductClick: (product: Product) => void;
    onSubProductClick: (
        subProduct: SubProduct,
        isSubOnly: boolean,
        lastHoveredRef: Product | undefined,
        lastHoveredVersionRef: ProductVersion | undefined
    ) => void;
    onVersionClick: (version: ProductVersion, lastHoveredRef: Product | undefined) => void;
    handleTopPosition: (refs: { current: (HTMLDivElement | null)[] }, index: number, versionsLength?: number) => string;

    // Additional props for doc type filter and other controls
    isWapUser?: boolean;
    isAdmin?: boolean;
    enableAllProductForAllUsers?: boolean;
    enableAllProductForUser?: boolean;
    docTypeFilter?: string;
    categories?: any[];
    chatModel?: string;
    aiModel_admin?: any[];
    reasoningEffort?: ReasoningEffort;
    activeAnalysisPanelTab?: any;
    isStreaming?: boolean;
    isLoading?: boolean;
    isAnswerLoading?: boolean;
    applyRestriction?: boolean;
    templateAppliedFlagValue?: boolean;
    activateTemplateTour: boolean;
    styles: Record<string, string>;

    // Event handlers for additional controls
    onDocTypeFilterChange?: (event: any, item: any) => void;
    onChatModelChange?: (event: any, item: any) => void;
    onReasoningEffortChange?: (event: any, item: any) => void;
    onConfigPanelToggle?: () => void;
    onTemplateIconClick?: () => void;
    onTemplateClear?: () => void;
    saveUserSettings?: (settings: any) => void;
    setActivateTemplateTour: (activate: boolean) => void;
}

interface TourState {
    run: boolean;
    steps: Step[];
}

/**
 * Normalises the `is_sub_only` flag. The backend emits the string "true" (or omits the key), but
 * callers also pass booleans, so accept both; anything else (undefined, null, false, "false", "")
 * is treated as a regular product. Used for both dropdown branches so they are exact complements.
 */
export const isSubOnlyProduct = (item?: { is_sub_only?: unknown } | null): boolean =>
    String(item?.is_sub_only ?? "")
        .trim()
        .toLowerCase() === "true";

export const ToolBarProductSettingOptions: React.FC<Props> = ({
    products,
    productText,
    subProductText,
    productVersionText,
    productIndexes,
    subProductIndexes,
    productVersionIndexes,
    windowSize,
    shouldDisableInputBox,
    onProductClick,
    onSubProductClick,
    onVersionClick,
    handleTopPosition,

    // Additional props
    isWapUser = false,
    isAdmin = false,
    enableAllProductForAllUsers = appInfo?.enable_all_product_for_all_users === true,
    enableAllProductForUser = userInfo?.enable_all_product !== false,
    docTypeFilter,
    categories = [],
    chatModel,
    aiModel_admin = [],
    reasoningEffort,
    activeAnalysisPanelTab,
    isStreaming = false,
    isLoading = false,
    isAnswerLoading = false,
    applyRestriction = false,
    templateAppliedFlagValue = false,
    activateTemplateTour = false,
    styles,

    // Event handlers
    onDocTypeFilterChange,
    onChatModelChange,
    onReasoningEffortChange,
    onConfigPanelToggle,
    onTemplateIconClick,
    onTemplateClear,
    saveUserSettings,
    setActivateTemplateTour
}) => {
    const [isMainDropdownOpen, setIsMainDropdownOpen] = useState(false);
    const [isSubDropdownOpen, setIsSubDropdownOpen] = useState(false);
    const [isVersionsOpen, setIsVersionsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(-1);
    const [isSubHovered, setIsSubHovered] = useState(-1);
    const [isVersionsHovered, setIsVersionsHovered] = useState(-1);
    const [subProducts, setSubProducts] = useState<SubProduct[]>([]);
    const [productVersions, setProductVersions] = useState<ProductVersion[]>([]);
    const [lastHoveredRef, setLastHoveredRef] = useState<Product | undefined>(undefined);
    const [lastVerHoveredRef, setLastVerHoveredRef] = useState<ProductVersion | undefined>(undefined);
    const [productSearchText, setProductSearchText] = useState("");
    const selectedLocale = sessionStorage?.getItem("selectedLocale") ?? "ja";
    const resolvedProductLocale = resolveProductLocale(selectedLocale, appInfo);
    const mainDropdownRef = useRef<HTMLDivElement>(null);
    const divProdFilterRefs = useRef<(HTMLDivElement | null)[]>([]);
    const divProdVersionFilterRefs = useRef<(HTMLDivElement | null)[]>([]);
    const dispatch = useDispatch();

    const resetNestedDropdownState = useCallback(() => {
        setIsHovered(-1);
        setIsSubHovered(-1);
        setIsVersionsHovered(-1);
        setSubProducts([]);
        setProductVersions([]);
        setIsSubDropdownOpen(false);
        setIsVersionsOpen(false);
        setLastHoveredRef(undefined);
        setLastVerHoveredRef(undefined);
    }, []);

    // Handle clicks outside the dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (mainDropdownRef.current && !mainDropdownRef.current.contains(event.target as Node)) {
                setIsMainDropdownOpen(false);
                resetNestedDropdownState();
                setProductSearchText("");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [resetNestedDropdownState]);

    const handleProductClick = (item: Product) => {
        onProductClick(item);
        setIsMainDropdownOpen(false);
        setIsSubDropdownOpen(false);
        setIsVersionsOpen(false);
        setProductSearchText("");
    };

    const handleSubProductClick = (item: SubProduct, isSubOnly: boolean) => {
        onSubProductClick(item, isSubOnly, lastHoveredRef, lastVerHoveredRef);
        setIsMainDropdownOpen(false);
        setIsSubDropdownOpen(false);
        setIsVersionsOpen(false);
        setProductSearchText("");
    };

    const handleVersionClick = (item: ProductVersion) => {
        onVersionClick(item, lastHoveredRef);
        setIsMainDropdownOpen(false);
        setIsSubDropdownOpen(false);
        setIsVersionsOpen(false);
        setProductSearchText("");
    };

    const isAllProductEnabledForCurrentUser = isAdmin ? enableAllProductForUser : enableAllProductForAllUsers && enableAllProductForUser;

    // Filter out ALL product unless it is enabled for the current user context
    const filteredProducts = isAllProductEnabledForCurrentUser
        ? products
        : products.filter((p: any) => p && p.text && p.text.toUpperCase() !== "ALL" && !(p.manual_key === "0" && p.faq_key === "0"));

    // Cross-hierarchy search filter: matches product name, version names, or sub-product names
    const searchFilteredProducts = useMemo(() => {
        if (!productSearchText.trim()) return filteredProducts;
        const q = productSearchText.trim().toLowerCase();
        return filteredProducts.filter((p: any) => {
            if ((p.text ?? "").toLowerCase().includes(q)) return true;
            if (Array.isArray(p.versions)) {
                for (const v of p.versions) {
                    if ((v.text ?? "").toLowerCase().includes(q)) return true;
                    if (Array.isArray(v.sub_versions)) {
                        for (const s of v.sub_versions) {
                            if ((s.text ?? "").toLowerCase().includes(q)) return true;
                        }
                    }
                }
            }
            return false;
        });
    }, [filteredProducts, productSearchText]);

    // Returns only the versions of a product that match the current search text
    const getFilteredVersionsForProduct = (product: Product): any[] => {
        const versions: any[] = product.versions ?? [];
        if (!productSearchText.trim()) return versions;
        const q = productSearchText.trim().toLowerCase();
        if ((product.text ?? "").toLowerCase().includes(q)) return versions;
        return versions.filter(
            (v: any) =>
                (v.text ?? "").toLowerCase().includes(q) ||
                (Array.isArray(v.sub_versions) && v.sub_versions.some((s: any) => (s.text ?? "").toLowerCase().includes(q)))
        );
    };

    // Returns only the sub-versions of a version that match the current search text
    const getFilteredSubsForVersion = (version: ProductVersion, product: Product | undefined): any[] => {
        const subs: any[] = version.sub_versions ?? [];
        if (!productSearchText.trim()) return subs;
        const q = productSearchText.trim().toLowerCase();
        if ((product?.text ?? "").toLowerCase().includes(q) || (version.text ?? "").toLowerCase().includes(q)) return subs;
        return subs.filter((s: any) => (s.text ?? "").toLowerCase().includes(q));
    };

    // For is_sub_only products: returns only the versions (used as sub-products) that match the search text
    const getFilteredSubsForSubOnly = (product: Product): any[] => {
        const subs: any[] = product.versions ?? [];
        if (!productSearchText.trim()) return subs;
        const q = productSearchText.trim().toLowerCase();
        if ((product.text ?? "").toLowerCase().includes(q)) return subs;
        return subs.filter((s: any) => (s.text ?? "").toLowerCase().includes(q));
    };

    const subProductsParentLabel = lastHoveredRef?.text ?? "";
    const versionsParentLabel = lastHoveredRef?.text ?? "";
    const subVersionsParentLabel = [lastHoveredRef?.text, lastVerHoveredRef?.text].filter(Boolean).join(" > ");

    const isNoProductSelected = !productIndexes?.manual_key && !productIndexes?.faq_key;
    const isProductSelectionMissing = isAllProductEnabledForCurrentUser
        ? !productIndexes?.manual_key && !productIndexes?.faq_key
        : productIndexes?.manual_key === "0" || productIndexes?.faq_key === "0";
    const allText = i18next.t("chat.ALL");
    const defaultProductFilterText = isAllProductEnabledForCurrentUser
        ? allText
        : `${i18next.t("chat.productFilterText")} - ${i18next.t("chat.sub_version")} - ...`;
    const computedDisplayText =
        productText === allText && (subProductText === allText || subProductText === "") && (productVersionText === allText || productVersionText === "")
            ? allText
            : productVersionText === ""
              ? subProductText !== ""
                  ? `${productText} - ${subProductText}`
                  : productText
              : `${productText} - ${productVersionText} - ${subProductText}`;
    const displayText = isNoProductSelected ? defaultProductFilterText : computedDisplayText || defaultProductFilterText;

    // CommandsContainerIconBtns component
    const CommandsContainerIconBtns = () => {
        const [tourTemplate, setTourTemplate] = useSetState<TourState>({
            run: (activateTemplateTour && userInfo.templateTourDone === "false" && userInfo.chatTourDone === "true") || activateTemplateTour,
            steps: [
                {
                    content: (
                        <div>
                            <a href={AIWorks_Guides + "#page=7"} target="_blank" className={toolbarStyles.tourLink} rel="noopener noreferrer">
                                {i18next.t(`appTour.templateTour.templatePrompt`)}
                            </a>
                        </div>
                    ),
                    target: "#TemplateIcon",
                    skipBeacon: true
                }
            ]
        });

        const fileUploadOrDeleteAPITriggerActive = useSelector((store: RootState) => store.questionInput.fileUploadOrDeleteAPITriggerActive);
        const suggestionsStage = useSelector(selectSuggestionsStage);
        const isSuggestionsActive = suggestionsStage !== "idle";
        // Called unconditionally so the hook order stays stable; the clear-template block below is
        // rendered only for some roles/states, so calling useMediaQuery inside it would violate the
        // rules of hooks.
        const isWideViewport = useMediaQuery("(min-width:1400px)");

        const handleJoyrideCallbackForTemplate = (data: any) => {
            const { status } = data;
            const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

            if (finishedStatuses.includes(status)) {
                endTourTemplate();
            }
            if (userInfo.templateTourDone === "true" && (userInfo.fileAttachmentTourDone === "false" || !userInfo.fileAttachmentTourDone)) {
                dispatch(QuestionInputReducerAction.updateActiveFileAttachmentTour(true));
            }
        };

        const endTourTemplate = useCallback(() => {
            setTourTemplate(prevState => ({
                ...prevState,
                run: false
            }));
            if (userInfo.templateTourDone === "false") {
                saveUserSettings?.({
                    chatTourDone: "true",
                    templateTourDone: "true",
                    fileAttachmentTourDone: userInfo.fileAttachmentTourDone,
                    answerTourDone: "false",
                    answerTabTourDone: "false"
                });
            }
            userInfo.templateTourDone = "true";
            setActivateTemplateTour(false);
            dispatch(QuestionInputReducerAction.updateActiveFileAttachmentTour(true));
        }, []);
        return (
            <Box sx={{ display: "flex", flexDirection: "row", whiteSpace: "pre", marginLeft: "auto" }}>
                <Joyride
                    continuous
                    steps={tourTemplate.steps}
                    onEvent={data => handleJoyrideCallbackForTemplate(data)}
                    run={tourTemplate.run}
                    scrollToFirstStep
                    locale={{
                        next: i18next.t(`appTour.next`),
                        skip: i18next.t(`appTour.skip`),
                        back: i18next.t(`appTour.back`),
                        last: i18next.t(`appTour.last`)
                    }}
                    styles={{
                        tooltip: {
                            padding: window.innerWidth < 1000 ? "10px" : "15px",
                            fontSize: window.innerWidth < 1000 ? "10px" : "16px"
                        },
                        tooltipContent: {
                            fontSize: window.innerWidth < 1000 ? "10px" : "16px"
                        }
                    }}
                    options={{
                        dismissKeyAction: false,
                        overlayClickAction: false,
                        blockTargetInteraction: true,
                        skipScroll: true,
                        spotlightPadding: 1,
                        zIndex: 1000000,
                        width: window.innerWidth < 1000 ? "200px" : "380px",
                        buttons: ["back", "primary", "skip"]
                    }}
                />
                <div
                    id="TemplateIcon"
                    className={`${styles?.commandContainerIconBtn || ""} ${toolbarStyles.templateIcon}`}
                    title={i18next.t("template.quickSearch")}
                >
                    {isWideViewport && !activeAnalysisPanelTab && (
                        <Button
                            onClick={onTemplateIconClick}
                            disabled={
                                applyRestriction ||
                                isStreaming ||
                                isLoading ||
                                isAnswerLoading ||
                                isProductSelectionMissing ||
                                fileUploadOrDeleteAPITriggerActive?.triggerActive ||
                                isSuggestionsActive
                            }
                            style={{
                                cursor:
                                    applyRestriction ||
                                    isStreaming ||
                                    isLoading ||
                                    isAnswerLoading ||
                                    isProductSelectionMissing ||
                                    fileUploadOrDeleteAPITriggerActive?.triggerActive ||
                                    isSuggestionsActive
                                        ? "not-allowed"
                                        : "pointer"
                            }}
                        >
                            <BookTemplate20Filled
                                primaryFill="#7376e1"
                                style={{ background: "none", color: "#7376e1", width: "25px", height: "18px", paddingLeft: "5px" }}
                            />
                            <Text style={{ padding: "0.25rem 0.5rem", fontSize: "12px" }}>{i18next.t("template.templatePromptIcon")}</Text>
                        </Button>
                    )}
                    {(!isWideViewport || activeAnalysisPanelTab) && (
                        <IconButton
                            style={{
                                background: "none",
                                cursor:
                                    applyRestriction ||
                                    isStreaming ||
                                    isLoading ||
                                    isAnswerLoading ||
                                    isProductSelectionMissing ||
                                    fileUploadOrDeleteAPITriggerActive?.triggerActive ||
                                    isSuggestionsActive
                                        ? "not-allowed"
                                        : "pointer"
                            }}
                            onClick={onTemplateIconClick}
                            disabled={
                                applyRestriction ||
                                isStreaming ||
                                isLoading ||
                                isAnswerLoading ||
                                isProductSelectionMissing ||
                                fileUploadOrDeleteAPITriggerActive?.triggerActive ||
                                isSuggestionsActive
                            }
                        >
                            <BookTemplate20Filled primaryFill="#7376e1" style={{ background: "none", color: "#7376e1", width: "25px", height: "25px" }} />
                        </IconButton>
                    )}
                </div>
                {(isAdmin || userInfo?.isWap === "True" || userInfo?.isWap === "true") && templateAppliedFlagValue && (
                    <div
                        id="TemplateClearIcon"
                        className={`${styles?.commandContainerIconBtn || ""} ${toolbarStyles.templateClearIcon}`}
                        title={i18next.t("template.templatePromptClearIcon")}
                    >
                        {isWideViewport && !activeAnalysisPanelTab && (
                            <Button
                                style={{
                                    cursor:
                                        applyRestriction ||
                                        isStreaming ||
                                        isLoading ||
                                        isAnswerLoading ||
                                        isProductSelectionMissing ||
                                        fileUploadOrDeleteAPITriggerActive?.triggerActive ||
                                        isSuggestionsActive
                                            ? "not-allowed"
                                            : "pointer"
                                }}
                                disabled={
                                    applyRestriction ||
                                    isStreaming ||
                                    isLoading ||
                                    isAnswerLoading ||
                                    isProductSelectionMissing ||
                                    fileUploadOrDeleteAPITriggerActive?.triggerActive ||
                                    isSuggestionsActive
                                }
                                onClick={onTemplateClear}
                            >
                                <FormatClearOutlined sx={{ background: "none", color: "#7376e1", width: "25px", height: "18px", paddingLeft: "5px" }} />
                                <Text style={{ padding: "0.25rem 0.5rem", fontSize: "12px" }}>{i18next.t("template.templatePromptClearIcon")}</Text>
                            </Button>
                        )}
                        {(!isWideViewport || activeAnalysisPanelTab) && (
                            <IconButton
                                style={{
                                    background: "none",
                                    cursor:
                                        applyRestriction ||
                                        isStreaming ||
                                        isLoading ||
                                        isAnswerLoading ||
                                        isProductSelectionMissing ||
                                        fileUploadOrDeleteAPITriggerActive?.triggerActive ||
                                        isSuggestionsActive
                                            ? "not-allowed"
                                            : "pointer"
                                }}
                                onClick={onTemplateClear}
                                disabled={
                                    applyRestriction ||
                                    isStreaming ||
                                    isLoading ||
                                    isAnswerLoading ||
                                    isProductSelectionMissing ||
                                    fileUploadOrDeleteAPITriggerActive?.triggerActive ||
                                    isSuggestionsActive
                                }
                            >
                                <FormatClearOutlined sx={{ background: "none", color: "#7376e1", width: "25px", height: "25px" }} />
                            </IconButton>
                        )}
                    </div>
                )}
            </Box>
        );
    };

    const handleMainDropdownClick = async () => {
        const isValid = await shouldDisableInputBox();
        if (isValid) {
            setIsMainDropdownOpen(!isMainDropdownOpen);
            setIsHovered(-1);
            setIsSubHovered(-1);
            setIsVersionsHovered(-1);
            setProductSearchText("");
            const product = appInfo.product_categories[resolvedProductLocale]?.filter(
                (item: any) => (item.manual_key && item.manual_key === productIndexes?.manual_key) || (item.faq_key && item.faq_key === productIndexes?.faq_key)
            );
            if (product?.length > 0) {
                if ((productVersionIndexes?.manual_version_key !== "" || productVersionIndexes?.faq_version_key !== "") && product[0]?.versions?.length > 0) {
                    setProductVersions([{ manual_version_key: "0", faq_version_key: "0", text: "ALL" }, ...(product[0]?.versions ?? [])]);
                    setIsVersionsOpen(true);
                    if (
                        (subProductIndexes.manual_sub_key !== "" || subProductIndexes.faq_sub_key !== "") &&
                        (productVersionIndexes?.manual_version_key !== "0" || productVersionIndexes?.faq_version_key !== "0")
                    ) {
                        const subVersion = product[0]?.versions?.filter(
                            (item: any) =>
                                (item.manual_version_key && productVersionIndexes?.manual_version_key === item.manual_version_key) ||
                                (item.faq_version_key && productVersionIndexes?.faq_version_key === item.faq_version_key)
                        );
                        if (subVersion[0]?.sub_versions?.length > 0) {
                            setSubProducts([{ manual_sub_key: "0", faq_sub_key: "0", text: "ALL" }, ...(subVersion[0]?.sub_versions ?? [])]);
                            setIsSubDropdownOpen(true);
                        }
                    }
                } else if ((subProductIndexes.manual_sub_key !== "" || subProductIndexes.faq_sub_key !== "") && product[0]?.versions?.length > 0) {
                    setSubProducts([{ manual_sub_key: "0", faq_sub_key: "0", text: "ALL" }, ...(product[0]?.versions ?? [])]);
                    setIsSubDropdownOpen(true);
                } else {
                    setSubProducts([]);
                    setProductVersions([]);
                    setIsSubDropdownOpen(false);
                    setIsVersionsOpen(false);
                }
            }
        }
    };

    // Pill-style dropdowns size to their selected text, with a floor so short labels still read as a pill
    // and a cap so an unusually long label cannot push the rest of the toolbar off-screen.
    const compactDropdownSx = useMemo(
        () => ({ width: "fit-content", minWidth: "90px", maxWidth: windowSize.width < 1100 ? "160px" : "260px" }),
        [windowSize.width]
    );

    const selectedChatModelText = useMemo(() => aiModel_admin.find(option => option?.key === chatModel)?.text ?? chatModel ?? "", [aiModel_admin, chatModel]);

    return (
        <>
            <div
                id="ToolBarProductSettingOptions"
                className={`Product Filter ${toolbarStyles.toolbarContainer} ${toolbarStyles.toolbarContainerExtended} ${
                    windowSize.width < 1100 ? toolbarStyles.toolbarContainerSmall : toolbarStyles.toolbarContainerLarge
                }`}
                style={{
                    display: shouldDisableInputBox() === false ? "none" : "flex"
                }}
            >
                <button
                    type="button"
                    onClick={handleMainDropdownClick}
                    className={`${toolbarStyles.mainDropdownClickable} ${
                        windowSize.width < 1100 ? toolbarStyles.mainDropdownClickableSmall : toolbarStyles.mainDropdownClickableLarge
                    }`}
                    title={displayText}
                    aria-expanded={isMainDropdownOpen}
                >
                    <span className={toolbarStyles.displayTextSpan}>{displayText}</span>

                    <ChevronDownRegular
                        style={{
                            position: "absolute",
                            right: "2px",
                            cursor: "pointer",
                            fontSize: "x-large",
                            height: "17px",
                            top: "3px"
                        }}
                    />
                </button>

                {isMainDropdownOpen && (
                    <div
                        ref={mainDropdownRef}
                        className={`${toolbarStyles.mainDropdownPanel} ${
                            windowSize.width < 1100 ? toolbarStyles.mainDropdownPanelSmall : toolbarStyles.mainDropdownPanelLarge
                        }`}
                    >
                        <div className={toolbarStyles.productSearchContainer}>
                            <input
                                type="text"
                                className={toolbarStyles.productSearchInput}
                                placeholder={i18next.t("chat.productSearchPlaceholder")}
                                value={productSearchText}
                                onChange={e => {
                                    setProductSearchText(e.target.value);
                                    resetNestedDropdownState();
                                }}
                                onClick={e => e.stopPropagation()}
                                autoFocus
                            />
                        </div>
                        <div className={toolbarStyles.mainDropdownContent}>
                            <ProductFilterDropdown
                                products={searchFilteredProducts}
                                productText={productText}
                                productVersionValue={productVersionText}
                                subProductValue={subProductText}
                                isHovered={isHovered}
                                isSubHovered={isSubHovered}
                                isVersionsHovered={isVersionsHovered}
                                setIsHovered={setIsHovered}
                                setIsSubHovered={setIsSubHovered}
                                setIsVersionsHovered={setIsVersionsHovered}
                                handleProductClick={handleProductClick}
                                handleVersionChange={(_option, _product) => {}}
                                handleSubProductChange={(_option, _product, _version) => {}}
                                handleTopPosition={handleTopPosition}
                                renderProductItem={(item, index) => {
                                    const productMatch = item?.manual_key === productIndexes?.manual_key && item?.faq_key === productIndexes?.faq_key;
                                    return (
                                        <div
                                            className={`${tabstyles.divOnHover} ${toolbarStyles.productItem} ${
                                                windowSize.width < 1100 ? toolbarStyles.productItemSmall : toolbarStyles.productItemLarge
                                            } ${
                                                isHovered === index
                                                    ? toolbarStyles.productItemHovered
                                                    : productMatch === true
                                                      ? toolbarStyles.productItemSelected
                                                      : toolbarStyles.productItemDefault
                                            }`}
                                            title={item?.text?.toString()}
                                            key={"index" + index}
                                            ref={el => {
                                                divProdFilterRefs.current[index] = el;
                                            }}
                                            onMouseEnter={() => {
                                                setIsHovered(index);
                                                setIsSubHovered(-1);
                                                setIsVersionsHovered(-1);
                                                setLastHoveredRef(item);
                                                setLastVerHoveredRef(undefined);
                                                if (isSubOnlyProduct(item) && item?.versions && item.versions.length > 0) {
                                                    const filteredSubs = getFilteredSubsForSubOnly(item);
                                                    const displaySubs = productSearchText.trim()
                                                        ? filteredSubs
                                                        : [{ manual_sub_key: "0", faq_sub_key: "0", text: "ALL" }, ...item.versions];
                                                    if (displaySubs.length > 0) {
                                                        setSubProducts(displaySubs);
                                                        setIsSubDropdownOpen(true);
                                                    } else {
                                                        setSubProducts([]);
                                                        setIsSubDropdownOpen(false);
                                                    }
                                                    setProductVersions([]);
                                                    setIsVersionsOpen(false);
                                                } else if (!isSubOnlyProduct(item) && item?.versions && item.versions.length > 0) {
                                                    const filteredVersions = getFilteredVersionsForProduct(item);
                                                    const displayVersions = productSearchText.trim()
                                                        ? filteredVersions
                                                        : [
                                                              {
                                                                  manual_version_key: "0",
                                                                  faq_version_key: "0",
                                                                  text: "ALL"
                                                              },
                                                              ...item.versions
                                                          ];
                                                    if (displayVersions.length > 0) {
                                                        setProductVersions(displayVersions);
                                                        setIsVersionsOpen(true);
                                                    } else {
                                                        setProductVersions([]);
                                                        setIsVersionsOpen(false);
                                                    }
                                                    setSubProducts([]);
                                                    setIsSubDropdownOpen(false);
                                                } else {
                                                    setSubProducts([]);
                                                    setProductVersions([]);
                                                    setIsSubDropdownOpen(false);
                                                    setIsVersionsOpen(false);
                                                }
                                            }}
                                            onClick={() => {
                                                handleProductClick(item);
                                            }}
                                        >
                                            {productMatch === true ? (
                                                <Text
                                                    className={`${toolbarStyles.FaqDropDownOptions} ${toolbarStyles.productTextSelected} ${
                                                        item?.isSelectable === "false" ? toolbarStyles.productTextDisabled : toolbarStyles.productTextEnabled
                                                    }`}
                                                >
                                                    {item?.text}
                                                </Text>
                                            ) : (
                                                <Text
                                                    className={`${toolbarStyles.FaqDropDownOptions} ${
                                                        item?.isSelectable === "false" ? toolbarStyles.productTextDisabled : toolbarStyles.productTextEnabled
                                                    }`}
                                                >
                                                    {item?.text}
                                                </Text>
                                            )}
                                        </div>
                                    );
                                }}
                            />
                        </div>

                        {/* Sub Products Dropdown */}
                        {isMainDropdownOpen && !isVersionsOpen && isSubDropdownOpen && (
                            <div
                                className={`${tabstyles.divOnHover} ${toolbarStyles.subProductsPanel} ${
                                    windowSize.width < 1100 ? toolbarStyles.subProductsPanelSmall : toolbarStyles.subProductsPanelLarge
                                }`}
                            >
                                {subProductsParentLabel && (
                                    <div className={toolbarStyles.dropdownContextHeader} title={subProductsParentLabel}>
                                        <Text className={toolbarStyles.dropdownContextText}>{subProductsParentLabel}</Text>
                                    </div>
                                )}
                                {subProducts.map((item, index) => {
                                    const subProdMatch =
                                        item?.manual_sub_key === subProductIndexes.manual_sub_key && item?.faq_sub_key === subProductIndexes.faq_sub_key;
                                    return (
                                        <div
                                            key={"index" + index}
                                            className={`${toolbarStyles.subProductItem} ${
                                                windowSize.width < 1100 ? toolbarStyles.subProductItemSmall : toolbarStyles.subProductItemLarge
                                            } ${
                                                isSubHovered === index
                                                    ? toolbarStyles.subProductItemHovered
                                                    : subProdMatch === true
                                                      ? toolbarStyles.subProductItemSelected
                                                      : toolbarStyles.subProductItemDefault
                                            }`}
                                            title={subProductsParentLabel ? `${item?.text?.toString()} (${subProductsParentLabel})` : item?.text?.toString()}
                                            onMouseEnter={() => {
                                                setIsSubHovered(index);
                                            }}
                                            onClick={() => {
                                                handleSubProductClick(item, true);
                                            }}
                                        >
                                            {subProdMatch === true ? (
                                                <Text className={`${toolbarStyles.FaqDropDownOptions} ${toolbarStyles.subProductTextBold}`}>{item?.text}</Text>
                                            ) : (
                                                <Text className={toolbarStyles.FaqDropDownOptions}>{item?.text}</Text>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Versions Dropdown */}
                        {isMainDropdownOpen && isVersionsOpen && (
                            <div
                                className={`${tabstyles.divOnHover} ${toolbarStyles.versionsPanelPositioned}`}
                                style={{
                                    top: "0px"
                                }}
                            >
                                {versionsParentLabel && (
                                    <div className={toolbarStyles.dropdownContextHeader} title={versionsParentLabel}>
                                        <Text className={toolbarStyles.dropdownContextText}>{versionsParentLabel}</Text>
                                    </div>
                                )}
                                <div className={toolbarStyles.versionsPanelContent}>
                                    {productVersions.map((item: ProductVersion, index) => {
                                        const versionMatch =
                                            item?.manual_version_key === productVersionIndexes?.manual_version_key &&
                                            item?.faq_version_key === productVersionIndexes?.faq_version_key;
                                        return (
                                            <div
                                                key={"index" + index}
                                                ref={el => {
                                                    divProdVersionFilterRefs.current[index] = el;
                                                }}
                                                className={`${toolbarStyles.versionItem} ${
                                                    isVersionsHovered === index
                                                        ? toolbarStyles.versionItemHovered
                                                        : versionMatch === true
                                                          ? toolbarStyles.versionItemSelected
                                                          : toolbarStyles.versionItemDefault
                                                }`}
                                                title={versionsParentLabel ? `${item?.text?.toString()} (${versionsParentLabel})` : item?.text?.toString()}
                                                onMouseEnter={() => {
                                                    setIsSubHovered(-1);
                                                    setIsVersionsHovered(index);
                                                    setLastVerHoveredRef(item);
                                                    if (item?.sub_versions && item.sub_versions.length > 0) {
                                                        const filteredSubs = getFilteredSubsForVersion(item, lastHoveredRef);
                                                        const displaySubs = productSearchText.trim()
                                                            ? filteredSubs
                                                            : [
                                                                  {
                                                                      manual_sub_key: "0",
                                                                      faq_sub_key: "0",
                                                                      text: "ALL"
                                                                  },
                                                                  ...item.sub_versions
                                                              ];
                                                        if (displaySubs.length > 0) {
                                                            setSubProducts(displaySubs);
                                                            setIsSubDropdownOpen(true);
                                                        } else {
                                                            setSubProducts([]);
                                                            setIsSubDropdownOpen(false);
                                                        }
                                                    } else {
                                                        setSubProducts([]);
                                                        setIsSubDropdownOpen(false);
                                                    }
                                                }}
                                                onClick={() => {
                                                    handleVersionClick(item);
                                                }}
                                            >
                                                {versionMatch === true ? (
                                                    <Text style={{ fontWeight: "bold", fontSize: "medium" }}>{item?.text}</Text>
                                                ) : (
                                                    <Text>{item?.text}</Text>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Sub Versions from Versions */}
                                {isVersionsOpen && isSubDropdownOpen && (
                                    <div
                                        className={`${tabstyles.divOnHover} ${toolbarStyles.subProductsPanel} ${
                                            windowSize.width < 1100 ? toolbarStyles.subProductsPanelSmall : toolbarStyles.subProductsPanelLarge
                                        }`}
                                    >
                                        {subVersionsParentLabel && (
                                            <div className={toolbarStyles.dropdownContextHeader} title={subVersionsParentLabel}>
                                                <Text className={toolbarStyles.dropdownContextText}>{subVersionsParentLabel}</Text>
                                            </div>
                                        )}
                                        {subProducts.map((item, index) => {
                                            const subProdMatch =
                                                item?.manual_sub_key === subProductIndexes.manual_sub_key &&
                                                item?.faq_sub_key === subProductIndexes.faq_sub_key;
                                            return (
                                                <div
                                                    key={"index" + index}
                                                    className={`${toolbarStyles.subProductItem} ${
                                                        windowSize.width < 1100 ? toolbarStyles.subProductItemSmall : toolbarStyles.subProductItemLarge
                                                    } ${
                                                        isSubHovered === index
                                                            ? toolbarStyles.subProductItemHovered
                                                            : subProdMatch === true
                                                              ? toolbarStyles.subProductItemSelected
                                                              : toolbarStyles.subProductItemDefault
                                                    }`}
                                                    title={
                                                        subVersionsParentLabel
                                                            ? `${item?.text?.toString()} (${subVersionsParentLabel})`
                                                            : item?.text?.toString()
                                                    }
                                                    onMouseEnter={() => {
                                                        setIsSubHovered(index);
                                                    }}
                                                    onClick={() => {
                                                        handleSubProductClick(item, false);
                                                    }}
                                                >
                                                    {subProdMatch === true ? (
                                                        <Text className={toolbarStyles.FaqDropDownOptions} style={{ fontWeight: "bold" }}>
                                                            {item?.text}
                                                        </Text>
                                                    ) : (
                                                        <Text className={toolbarStyles.FaqDropDownOptions}>{item?.text}</Text>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Document Type Filter */}
            {isWapUser && (
                <Box sx={{ margin: "0px 8px", ...compactDropdownSx }}>
                    <Dropdown
                        id="doc_type_filter"
                        title={i18next.t("chat.documenttype")}
                        selectedKey={docTypeFilter}
                        placeholder={i18next.t("chat.documenttype")}
                        options={categories}
                        dropdownWidth="auto"
                        onChange={(event, item) => {
                            if (onDocTypeFilterChange) {
                                onDocTypeFilterChange(event, item);
                            }
                            if (saveUserSettings) {
                                saveUserSettings({
                                    docValue: item?.key?.toString() ?? ""
                                });
                            }
                            if (userInfo) {
                                userInfo.docValue = item?.key?.toString() ?? "";
                            }
                        }}
                        onRenderOption={item => {
                            return (
                                <Text
                                    className={styles.documentCategories || ""}
                                    title={i18next.t(`${item?.text ?? undefined}`)}
                                    style={{
                                        padding: "8px",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis"
                                    }}
                                >
                                    {i18next.t(`${item?.text ?? undefined}`)}
                                </Text>
                            );
                        }}
                        style={{
                            fontSize: windowSize.width < 1100 ? "10px" : "15px"
                        }}
                        styles={{
                            title: {
                                border: "2px solid rgba(0,0,0,.1) !important",
                                borderRadius: "1.5rem !important"
                            }
                        }}
                    />
                </Box>
            )}

            {/* Chat Model Dropdown */}
            {isAdmin && (
                <Box sx={compactDropdownSx}>
                    <Dropdown
                        title={selectedChatModelText ? `${i18next.t("chat.chatModel")}: ${selectedChatModelText}` : i18next.t("chat.chatModel")}
                        className={styles.aimodeloptions || ""}
                        selectedKey={chatModel}
                        options={aiModel_admin}
                        onChange={onChatModelChange}
                        dropdownWidth="auto"
                        styles={{
                            title: {
                                border: "2px solid rgba(0,0,0,.1) !important",
                                borderRadius: "1.5rem !important"
                            }
                        }}
                    />
                </Box>
            )}

            {/* Reasoning Effort Dropdown */}
            {chatModel && isReasoningModel(chatModel) && (
                <Box sx={compactDropdownSx}>
                    <Dropdown
                        title={i18next.t("reasoningEffort.title")}
                        className={styles.aimodeloptions || ""}
                        selectedKey={reasoningEffort}
                        dropdownWidth="auto"
                        options={getReasoningEffortsForModel(chatModel).map(effort => ({
                            key: effort,
                            text: i18next.t(`reasoningEffort.${effort}`),
                            data: effort,
                            title: i18next.t(`reasoningEffort.${effort}Tooltip`)
                        }))}
                        onChange={onReasoningEffortChange}
                        onRenderOption={item => {
                            if (!item) return null;
                            const icons: Record<string, string> = {
                                none: "🚫",
                                minimal: "⚡",
                                low: "🏃",
                                medium: "⚖️",
                                high: "🧠"
                            };
                            return (
                                <div
                                    className={`${toolbarStyles.reasoningEffortOption} ${
                                        windowSize.width < 1100 ? toolbarStyles.reasoningEffortOptionSmall : toolbarStyles.reasoningEffortOptionLarge
                                    }`}
                                    title={item.title}
                                >
                                    <span className={toolbarStyles.iconSpan}>{icons[item.key as keyof typeof icons]}</span>
                                    <span className={toolbarStyles.labelSpan}>{item.text}</span>
                                </div>
                            );
                        }}
                        styles={{
                            title: {
                                border: "2px solid rgba(0,0,0,.1) !important",
                                borderRadius: "1.5rem !important",
                                fontSize: windowSize.width < 1100 ? "10px" : "15px"
                            },
                            dropdownItem: {
                                minHeight: "40px"
                            },
                            callout: {
                                minWidth: "180px"
                            }
                        }}
                    />
                </Box>
            )}

            {/* Developer Settings Button */}
            {isAdmin && (
                <div title={i18next.t("chat.developerSettings")} className={toolbarStyles.developerSettingsButton}>
                    <SettingsButton
                        onClick={onConfigPanelToggle ?? (() => undefined)}
                        containerStyle={{ paddingLeft: 0 }}
                        buttonStyle={{
                            backgroundColor: "transparent",
                            border: "none",
                            minWidth: "auto",
                            height: "auto",
                            padding: 0
                        }}
                        iconClassName={tabstyles.commandSettingsButton || ""}
                    />
                </div>
            )}

            {/* Commands Container Icons */}
            <CommandsContainerIconBtns />
        </>
    );
};

export type { Product, ProductVersion, SubProduct };
